import PropTypes from "prop-types";
import React from "react";
import EthnicitySelect from "./ethnicitySelect";
import { getBase64Image, getPixelCrop } from "./utils";
import { Button, Loader } from "cxa-components";
import { intlShape, injectIntl } from "react-intl";
import { Notification } from "@cxagroup/casper";
import messages from "components/messagesDescription";
import ReactCrop from "react-image-crop";
import classnames from "classnames";
import Webcam from "../../common/WebCam";

const TakePhotoAndCropping = ({
  mode,
  uploadImageResponse,
  screenshot,
  onChange,
  onImageLoaded,
  onComplete,
  crop,
  setWebcam,
  setPhoto,
  onFailure,
}) => {
  if (mode === "crop" || mode === "readyToCrop") {
    return uploadImageResponse ? (
      <ReactCrop
        ref={el => setPhoto(el)}
        src={uploadImageResponse.image}
        keepSelection={true}
        crop={crop}
        onChange={onChange}
        onImageLoaded={onImageLoaded}
        onComplete={onComplete}
      />
    ) : (
      <Loader />
    );
  }

  if (mode === "confirming" && screenshot) {
    return (
      <div>
        <div className="u-hidden">
          <ReactCrop
            ref={el => setPhoto(el)}
            src={screenshot}
            keepSelection={false}
            crop={crop}
            onChange={onChange}
            onImageLoaded={onImageLoaded}
            onComplete={onComplete}
          />
        </div>
        <Loader />
      </div>
    );
  }

  if (mode === "taking" && !screenshot) {
    return (
      <div className="apl-photo__camera-wrapper">
        <Webcam
          name="webcam"
          ref={el => setWebcam(el)}
          width="640"
          height="480"
          audio={false}
          onFailure={onFailure}
        />
        <div className="apl-photo__overlay apl-photo__overlay--active">
          <img src="/assets/images/wellness/face-outline.png" alt="" />
        </div>
      </div>
    );
  }

  if (mode === "init") {
    return (
      <div className="apl-photo__overlay">
        <img src="/assets/images/wellness/face-outline.png" alt="" />
      </div>
    );
  }

  return null;
};

class TakePhotoForm extends React.PureComponent {
  constructor() {
    super();

    this.state = {
      mode: "init", // init, taking, confirming
      screenshot: null,
      imgCanvas: null,
      crop: null,
      pixelCrop: null,
      error: null,
    };

    this.image = null;
    this.webcam = null;

    this.handleSwitchToTakePhoto = this.handleSwitchToTakePhoto.bind(this);
    this.handleTakePhoto = this.handleTakePhoto.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleRetake = this.handleRetake.bind(this);
    this.handleConfirm = this.handleConfirm.bind(this);
    this.setWebcam = this.setWebcam.bind(this);
  }

  setWebcam(webcam) {
    this.webcam = webcam;
  }

  onImageToCropLoaded = () => this.setState({ mode: "readyToCrop" });

  setPhoto = image => {
    if (image && image.imageRef) {
      this.image = image;
      image.imageRef.onload = this.onImageToCropLoaded;
    }
  };

  handleImageLoaded = image => {
    this.naturalImage = image;
    const cropDefaultValues = { x: 26, y: 2, height: 93, width: 49 };

    this.setState({
      crop: cropDefaultValues,
      pixelCrop: getPixelCrop(image, cropDefaultValues),
    });
  };

  // Use common util to compute the pixelCrop values
  // Need to investigate further why pixelCrop values from plugin is incorrect
  handleImageCropOnChange = crop => {
    this.setState({
      crop,
      pixelCrop: getPixelCrop(this.naturalImage, crop),
    });
  };

  handleCropComplete = crop => {
    this.setState({
      crop,
      pixelCrop: getPixelCrop(this.naturalImage, crop),
    });
  };

  handleSwitchToTakePhoto() {
    this.setState({ mode: "taking", screenshot: null });
  }

  handleTakePhoto() {
    const screenshot = this.webcam.getScreenshot();
    this.setState({ screenshot, mode: "confirming" });
  }

  handleRetake() {
    this.setState({ screenshot: null, mode: "taking" });
  }

  handleCancel() {
    this.setState({ mode: "init" });
  }

  handleConfirm() {
    const {
      pixelCrop,
      pixelCrop: { x, y, height, width },
    } = this.state;
    const { uploadImageResponse } = this.props;

    const cropMetadata = {
      x,
      y,
      height,
      width,
    };
    //display image is responsive, not the one stored server-side hence natural resolutions are needed here to work out crop co-ordinates on stored image
    const ratio = this.naturalImage
      ? this.naturalImage.naturalHeight / this.naturalImage.height
      : 1;
    const naturalCropMetadata = Object.keys(cropMetadata).reduce(
      (prev, key) => ({
        ...prev,
        // API can't take real numbers, only ints so we do some rounding here. If black border appears somehow, because of extra 1px from rounding up then need to revise API to accept floats for precison
        [key]: Math.round(cropMetadata[key] * ratio),
      }),
      {}
    );

    if (pixelCrop && uploadImageResponse) {
      // Predefined value for photo taken from camera. in order to crop the center part.
      this.props.confirmCameraPhoto(
        uploadImageResponse.filename,
        naturalCropMetadata,
        this.props.ethnicity
      );
    }
  }

  // eslint-disable-next-line react/no-deprecated
  componentWillUpdate(nextProps, nextState) {
    if (this.image && this.image.imageRef && nextState.mode === "confirming") {
      nextProps.uploadImage({ image: getBase64Image(this.image) });
      this.setState({ mode: "crop" });
    }
  }

  handleWebcamError = error => {
    const { intl } = this.props;

    this.setState({
      error: intl.formatMessage(messages.webcamFailed, {
        message: error.message,
        name: error.name,
      }),
    });
  };

  render() {
    const { intl } = this.props;
    const { mode, screenshot, crop, error } = this.state;
    const { uploadImageResponse } = this.props;
    const isTransitionDuringImageUpload =
      (mode === "crop" || mode === "confirming") && !uploadImageResponse;

    let buttons;
    //TODO - lift switch cases into custom Components
    switch (mode) {
      case "taking":
        buttons = (
          <p>
            <Button primary onClick={this.handleTakePhoto}>
              {intl.formatMessage(messages.takePhotoDescription)}
            </Button>
            <Button secondary onClick={this.props.handleSkip}>
              {intl.formatMessage(messages.skipTextDescription)}
            </Button>
            <Button secondary onClick={this.handleCancel}>
              {intl.formatMessage(messages.cancelTextDescription)}
            </Button>
          </p>
        );
        break;

      case "crop":
      case "readyToCrop":
        buttons = (
          <p>
            <Button primary submitting={this.props.submitting} onClick={this.handleConfirm}>
              {intl.formatMessage(messages.confirmTextDescription)}
            </Button>
            <Button secondary onClick={this.handleRetake}>
              {intl.formatMessage(messages.retakeTextDescription)}
            </Button>
            <Button secondary onClick={this.handleCancel}>
              {intl.formatMessage(messages.cancelTextDescription)}
            </Button>
          </p>
        );
        break;

      case "init":
        buttons = (
          <p>
            <Button primary onClick={this.handleSwitchToTakePhoto}>
              {intl.formatMessage(messages.takePhotoDescription)}
            </Button>
            <Button primary onClick={this.props.handleSwitchToUploadPhoto}>
              {intl.formatMessage(messages.uploadPhotoTextDescription)}
            </Button>
            <Button secondary onClick={this.props.handleSkip}>
              {intl.formatMessage(messages.skipTextDescription)}
            </Button>
          </p>
        );
        break;

      default:
        break;
    }

    return (
      <article className="card">
        <section id="hrawebcam-container" className="apl-photo__container">
          <div
            id="hrawebcam"
            className={classnames(
              "apl-photo__camera",
              `apl-photo__camera--${mode}`,
              isTransitionDuringImageUpload && "apl-photo__camera--crop-without-image"
            )}
          >
            {/*mode-dependent rendering in this component...*/}
            <TakePhotoAndCropping
              mode={mode}
              crop={crop}
              screenshot={screenshot}
              uploadImageResponse={uploadImageResponse}
              onChange={this.handleImageCropOnChange}
              onImageLoaded={this.handleImageLoaded}
              onComplete={this.handleCropComplete}
              setPhoto={this.setPhoto}
              setWebcam={this.setWebcam}
              onFailure={this.handleWebcamError}
            />
          </div>
          {error && <Notification level={Notification.level.error} message={error} />}
        </section>
        <footer>
          {(mode === "crop" || mode === "readyToCrop") && (
            <EthnicitySelect
              ethnicity={this.props.ethnicity}
              handleChangeEthnicity={this.props.handleChangeEthnicity}
            />
          )}
          {/*...and some more here*/}
          {buttons}
        </footer>
      </article>
    );
  }
}

TakePhotoForm.propTypes = {
  handleSkip: PropTypes.func,
  handleSwitchToUploadPhoto: PropTypes.func,
  handleChangeEthnicity: PropTypes.func,
  handleImageLoaded: PropTypes.func,
  handleCropComplete: PropTypes.func,
  uploadImage: PropTypes.func,
  ethnicity: PropTypes.node,
  confirmCameraPhoto: PropTypes.func,
  intl: intlShape.isRequired,
  submitting: PropTypes.bool,
  uploadImageResponse: PropTypes.object,
};

TakePhotoAndCropping.propTypes = {
  mode: PropTypes.string,
  screenshot: PropTypes.string,
  uploadImageResponse: PropTypes.object,
  onChange: PropTypes.func,
  onImageLoaded: PropTypes.func,
  onComplete: PropTypes.func,
  crop: PropTypes.object,
  setPhoto: PropTypes.func,
  setWebcam: PropTypes.func,
  onFailure: PropTypes.func,
};

export default injectIntl(TakePhotoForm);



// WEBPACK FOOTER //
// ./components/wellness/lifestyleAgeing/TakePhotoForm.js